import React, { useContext } from 'react';
import {
  MdOutlineEventAvailable,
  MdOutlineLocalShipping,
  MdOutlineVerified,
  MdSupportAgent,
} from 'react-icons/md';

import { NonCommerceInfoContext, ProductContainerContext } from '@rsc/contexts';
import calculateDateEstimateRange from '@rsc/utils/calculateDateEstimateRange';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';

interface Props {
  shipping: number;
  returnDays: number;
  deliveryTimeEstimate?: {
    minDeliveryDays?: number;
    maxDeliveryDays?: number;
  };
  isIFB?: boolean;
}

export default function ProductMessaging({
  shipping,
  returnDays,
  deliveryTimeEstimate,
  isIFB = false,
}: Readonly<Props>) {
  // @ts-expect-error will need a refactor
  const { isNonCommerce } = useContext(NonCommerceInfoContext);
  // @ts-expect-error will need a refactor
  const { activeVariant, productLifecycle } = useContext(
    ProductContainerContext
  );
  const { tt, locale, localizePrice } = useUiComponentsInternationalContext();

  if (
    isNonCommerce ||
    productLifecycle === 'Obsolete' ||
    activeVariant?.variantLifecycle === 'Obsolete'
  )
    return null;

  const svgStyling = 'fill-muted-foreground shrink-0';
  const paraStyling = `text-foreground text-sm font-light mb-0 leading-4`;
  const linkStyling = `underline hover:no-underline text-foreground-link`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <MdOutlineEventAvailable size="16px" className={svgStyling} />
        <p className={paraStyling}>
          {tt(s => s.productOptionsComponent.orderTodayLabel, {
            range: calculateDateEstimateRange(deliveryTimeEstimate, locale),
          })}
        </p>
      </div>

      {shipping ? (
        <div className="flex gap-2">
          <MdOutlineLocalShipping size="16px" className={svgStyling} />
          {activeVariant?.price > parseInt(shipping.toString(), 10) ? (
            <p className={paraStyling}>
              {tt(
                s => s.productOptionsComponent.qualifiesForFreeShippingLabel,
                {
                  link: (
                    <LocalizedLink
                      className={linkStyling}
                      to="/support/policies-and-terms/shipping-policy"
                    >
                      {tt(s => s.productOptionsComponent.freeShippingLink)}!
                    </LocalizedLink>
                  ),
                }
              )}
            </p>
          ) : (
            <p className={paraStyling}>
              {tt(s => s.productOptionsComponent.onOrdersOverLabel, {
                link: (
                  <LocalizedLink
                    className={linkStyling}
                    to="/support/policies-and-terms/shipping-policy"
                  >
                    {tt(s => s.productOptionsComponent.freeShippingLink)}
                  </LocalizedLink>
                ),
                shipping: localizePrice(shipping, 0),
              })}
            </p>
          )}
        </div>
      ) : null}

      {!!returnDays && (
        <div className="flex gap-2">
          <MdOutlineVerified size="16px" className={svgStyling} />
          <p className={paraStyling}>
            <b className="font-semibold">
              {tt(s => s.productOptionsComponent.satisfactionGuaranteedLabel)}
            </b>{' '}
            <LocalizedLink
              className={linkStyling}
              to="/support/policies-and-terms/return-and-warranty-information"
            >
              {tt(s => s.productOptionsComponent.returnLink)}
            </LocalizedLink>{' '}
            {tt(s => s.productOptionsComponent.withinLabel, {
              returnDays: returnDays.toString(),
            })}
          </p>
        </div>
      )}

      {isIFB && (
        <div className="flex gap-2">
          <MdSupportAgent size="16px" className={svgStyling} />
          <p className={paraStyling}>
            {tt(s => s.productOptionsComponent.IFBMessageLabel, {
              care: (
                <b className="font-bold">
                  {tt(s => s.productOptionsComponent.USCustomerCare)}
                </b>
              ),
            })}
          </p>
        </div>
      )}
    </div>
  );
}
